import { useEffect, useRef, useState } from "react";
import { Link, useOutletContext } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, MONTHS } from "../api";
import type {
  DraftChoice,
  Me,
  OrderChild,
  OrderDay,
  OrderPayload,
  OrderPrefillPayload,
} from "../types";
import { Icon } from "../components/Icon";
import { Modal } from "../components/Modal";
import { useToast } from "../components/Toast";
import { Button, EmptyState, Field, PageHeader, Skeleton } from "../components/ui";

const BUYING: DraftChoice[] = ["Hot", "Swap", "Veggie"];
const SHORT: Record<DraftChoice, string> = {
  Hot: "Hot",
  Swap: "Swap",
  Veggie: "Veggie",
  "No Lunch": "Skip",
};

type Pick = { choice: DraftChoice; extraSlice?: boolean };
type Picks = Record<string, Pick>;
type LocalDraft = { picks: Picks; milk: string | null };

const pad = (n: number) => String(n).padStart(2, "0");
const todayIso = () => {
  const n = new Date();
  return `${n.getFullYear()}-${pad(n.getMonth() + 1)}-${pad(n.getDate())}`;
};
const dishOf = (d: OrderDay, c: DraftChoice) =>
  c === "Hot" ? d.dishes.hot : c === "Swap" ? d.dishes.swap : d.dishes.veggie;

function unsetCount(days: OrderDay[], picks: Picks): number {
  return days.filter((d) => !d.noSchool && d.choices.length > 0 && !picks[d.date])
    .length;
}

/** Do a working draft and a submitted order describe the same choices + milk? */
function samePicks(
  aPicks: Picks,
  bPicks: Picks,
  aMilk: string | null,
  bMilk: string | null,
): boolean {
  if ((aMilk ?? "") !== (bMilk ?? "")) return false;
  const ak = Object.keys(aPicks);
  if (ak.length !== Object.keys(bPicks).length) return false;
  return ak.every((k) => {
    const x = aPicks[k];
    const y = bPicks[k];
    return !!y && x.choice === y.choice && !!x.extraSlice === !!y.extraSlice;
  });
}

function mondayOf(dateStr: string): string {
  const d = new Date(`${dateStr}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() - ((d.getUTCDay() + 6) % 7));
  return d.toISOString().slice(0, 10);
}
function weekLabel(days: OrderDay[]): string {
  const mon = (s: string) => MONTHS[Number(s.slice(5, 7)) - 1].slice(0, 3);
  const dnum = (s: string) => Number(s.slice(8));
  const a = days[0].date;
  const b = days[days.length - 1].date;
  return mon(a) === mon(b)
    ? `${mon(a)} ${dnum(a)}–${dnum(b)}`
    : `${mon(a)} ${dnum(a)} – ${mon(b)} ${dnum(b)}`;
}
function byWeek(days: OrderDay[]): { key: string; label: string; days: OrderDay[] }[] {
  const groups = new Map<string, OrderDay[]>();
  for (const d of days) {
    const k = mondayOf(d.date);
    let arr = groups.get(k);
    if (!arr) groups.set(k, (arr = []));
    arr.push(d);
  }
  return [...groups.entries()]
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([key, ds]) => ({ key, label: weekLabel(ds), days: ds }));
}

export function Order() {
  const me = useOutletContext<Me>();
  const enabled = me.isAdmin || me.orderAssist;
  const toast = useToast();
  const qc = useQueryClient();

  const [{ year, month }, setYm] = useState(() => {
    const n = new Date();
    return { year: n.getFullYear(), month: n.getMonth() + 1 };
  });
  const [showSetup, setShowSetup] = useState(false);
  const [local, setLocal] = useState<Record<number, LocalDraft>>({});
  const [saveState, setSaveState] = useState<"idle" | "saving" | "saved">("idle");
  const [scope, setScope] = useState<number>(0); // 0 = all kids
  // Submitted children the parent has re-opened to change before the due date.
  const [editing, setEditing] = useState<Set<number>>(new Set());
  const stopEditing = (id: number) =>
    setEditing((cur) => {
      const next = new Set(cur);
      next.delete(id);
      return next;
    });

  const localRef = useRef(local);
  localRef.current = local;
  const dirty = useRef<Set<number>>(new Set());
  const timer = useRef<ReturnType<typeof setTimeout>>();

  const q = useQuery({
    queryKey: ["order-draft", year, month],
    queryFn: () =>
      api.get<OrderPayload>(`/order/draft?year=${year}&month=${month}`),
    enabled,
  });
  const prefill = useQuery({
    queryKey: ["order-prefill", year, month],
    queryFn: () =>
      api.get<OrderPrefillPayload>(`/order/prefill?year=${year}&month=${month}`),
    enabled: enabled && !!q.data?.form,
  });
  const formMonths = useQuery({
    queryKey: ["order-form-months"],
    queryFn: () =>
      api.get<{ latest: { year: number; month: number } | null }>(
        "/order/forms/months",
      ),
    enabled,
  });
  const markOpened = useMutation({
    mutationFn: (childRuleId: number) =>
      api.post(`/order/draft/${childRuleId}/opened`, { year, month }),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: ["order-prefill", year, month] }),
  });

  // Discard a re-opened order: drop the working draft (a trailing autosave for
  // this child is cancelled first so it can't rewrite it), snap the picker back
  // to the submitted order, and re-lock.
  const discardEdits = useMutation({
    mutationFn: (childRuleId: number) => {
      dirty.current.delete(childRuleId);
      if (dirty.current.size === 0) clearTimeout(timer.current);
      return api.del(
        `/order/draft/${childRuleId}?year=${year}&month=${month}`,
      );
    },
    onSuccess: (_r, id) => {
      const c = q.data?.children.find((x) => x.childRuleId === id);
      if (c?.submitted) {
        setLocal((l) => ({
          ...l,
          [id]: { picks: { ...c.submitted!.picks }, milk: c.submitted!.milk },
        }));
      }
      stopEditing(id);
      qc.invalidateQueries({ queryKey: ["order-draft", year, month] });
      qc.invalidateQueries({ queryKey: ["order-prefill", year, month] });
    },
    onError: (e) => toast.error((e as Error).message),
  });

  const dismissMismatch = useMutation({
    mutationFn: (childRuleId: number) =>
      api.post(`/order/draft/${childRuleId}/mismatch/dismiss`, { year, month }),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: ["order-draft", year, month] }),
    onError: (e) => toast.error((e as Error).message),
  });

  const saveMut = useMutation({
    mutationFn: (ids: number[]) => {
      const children: Record<string, LocalDraft> = {};
      for (const id of ids) {
        if (localRef.current[id]) children[id] = localRef.current[id];
      }
      return api.put(`/order/draft`, { year, month, children });
    },
    onMutate: () => setSaveState("saving"),
    onSuccess: () => {
      setSaveState("saved");
      qc.invalidateQueries({ queryKey: ["order-prefill", year, month] });
      setTimeout(() => setSaveState("idle"), 1500);
    },
    onError: (e) => {
      setSaveState("idle");
      toast.error((e as Error).message);
    },
  });

  useEffect(() => {
    if (!q.data) return;
    const seed: Record<number, LocalDraft> = {};
    for (const c of q.data.children) {
      // A submitted child mirrors what the school has; its working draft is
      // only shown once the parent explicitly resumes an edit (beginEdit).
      seed[c.childRuleId] = c.submitted
        ? { picks: { ...c.submitted.picks }, milk: c.submitted.milk }
        : { picks: { ...c.picks }, milk: c.milk };
    }
    setLocal(seed);
  }, [q.data]);

  useEffect(() => () => clearTimeout(timer.current), []);

  // On first load, jump to the newest month that has a registered order form.
  // `ymTouched` stays false until the user navigates months themselves.
  const ymTouched = useRef(false);
  useEffect(() => {
    if (ymTouched.current || !formMonths.isSuccess) return;
    ymTouched.current = true;
    const latest = formMonths.data?.latest;
    if (latest && (latest.year !== year || latest.month !== month)) {
      setYm({ year: latest.year, month: latest.month });
    }
  }, [formMonths.isSuccess, formMonths.data, year, month]);

  function scheduleSave(ids: number[]) {
    ids.forEach((id) => dirty.current.add(id));
    clearTimeout(timer.current);
    timer.current = setTimeout(() => {
      const pending = [...dirty.current];
      dirty.current.clear();
      if (pending.length) saveMut.mutate(pending);
    }, 700);
  }

  function mutateChildren(ids: number[], fn: (d: LocalDraft) => LocalDraft) {
    const targets = ids.filter((id) => !lockedIds.has(id));
    if (targets.length === 0) return;
    setLocal((l) => {
      const next = { ...l };
      for (const id of targets) if (next[id]) next[id] = fn(next[id]);
      return next;
    });
    scheduleSave(targets);
  }

  const data = q.data;
  const days = data?.days ?? [];
  const children = data?.children ?? [];
  // After the due date the whole month locks, for everyone, enforced server-side
  // too. Before it, a submitted child stays locked until the parent explicitly
  // hits "Edit order" (tracked in `editing`); a leftover divergent draft does
  // not unlock it, it only raises an "unsent changes" prompt.
  const formClosed = !!data?.form?.closed;

  const isEditing = (c: OrderChild) =>
    !!c.submitted && !formClosed && editing.has(c.childRuleId);
  const hasUnsentDraft = (c: OrderChild) =>
    !!c.submitted &&
    !formClosed &&
    !editing.has(c.childRuleId) &&
    c.draftAfterSubmit &&
    Object.keys(c.picks).length > 0 &&
    !samePicks(c.picks, c.submitted.picks, c.milk, c.submitted.milk);

  const lockedIds = new Set(
    children
      .filter(
        (c) => formClosed || (!!c.submitted && !editing.has(c.childRuleId)),
      )
      .map((c) => c.childRuleId),
  );
  const editedIds = new Set(children.filter(isEditing).map((c) => c.childRuleId));
  const unsentIds = new Set(
    children.filter(hasUnsentDraft).map((c) => c.childRuleId),
  );
  // Both states need the same "resubmit on Forms" affordance.
  const needsResubmit = new Set([...editedIds, ...unsentIds]);

  const beginEdit = (c: OrderChild, from: "submitted" | "draft") => {
    setLocal((l) => ({
      ...l,
      [c.childRuleId]:
        from === "draft"
          ? { picks: { ...c.picks }, milk: c.milk }
          : { picks: { ...c.submitted!.picks }, milk: c.submitted!.milk },
    }));
    setEditing((cur) => new Set(cur).add(c.childRuleId));
  };
  const editableIds = children
    .map((c) => c.childRuleId)
    .filter((id) => !lockedIds.has(id));
  const allLocked = children.length > 0 && editableIds.length === 0;
  const scopedIds = (scope ? [scope] : editableIds).filter(
    (id) => !lockedIds.has(id),
  );

  const setKidPick = (id: number, date: string, choice: DraftChoice | null) =>
    mutateChildren([id], (d) => {
      const picks = { ...d.picks };
      if (choice === null) delete picks[date];
      else
        picks[date] = {
          choice,
          ...(choice === "Hot" && picks[date]?.extraSlice
            ? { extraSlice: true }
            : {}),
        };
      return { ...d, picks };
    });

  const setKidExtra = (id: number, date: string, on: boolean) =>
    mutateChildren([id], (d) => {
      const cur = d.picks[date];
      if (!cur) return d;
      return {
        ...d,
        picks: { ...d.picks, [date]: { ...cur, extraSlice: on || undefined } },
      };
    });

  const bulkFill = (choice: DraftChoice, onlyBlanks: boolean) =>
    mutateChildren(scopedIds, (d) => {
      const picks = { ...d.picks };
      for (const day of days) {
        if (day.noSchool || !day.choices.includes(choice)) continue;
        if (onlyBlanks && picks[day.date]) continue;
        picks[day.date] = { choice };
      }
      return { ...d, picks };
    });

  const bulkClear = () => mutateChildren(scopedIds, (d) => ({ ...d, picks: {} }));

  if (!enabled) {
    return (
      <>
        <PageHeader title="Order lunch" />
        <EmptyState icon="utensils" title="Coming soon">
          The order picker isn't switched on yet.
        </EmptyState>
      </>
    );
  }

  const monthLabel = `${MONTHS[month - 1]} ${year}`;
  const shift = (delta: number) => {
    ymTouched.current = true;
    const d = new Date(Date.UTC(year, month - 1 + delta, 1));
    setYm({ year: d.getUTCFullYear(), month: d.getUTCMonth() + 1 });
  };

  return (
    <>
      <PageHeader
        title="Order lunch"
        desc="Go through the month once and pick for every child on each day."
        action={
          <div className="cluster">
            <Button
              size="sm"
              variant="ghost"
              icon="arrow-left"
              onClick={() => shift(-1)}
            >
              <span className="hide-sm">Prev</span>
            </Button>
            <strong style={{ minWidth: 120, textAlign: "center" }}>
              {monthLabel}
            </strong>
            <Button size="sm" variant="ghost" onClick={() => shift(1)}>
              <span className="hide-sm">Next</span>{" "}
              <Icon name="arrow-right" size={15} />
            </Button>
          </div>
        }
      />

      {me.isAdmin && (
        <div className="card order-setup">
          <div className="between">
            <div>
              <strong>Order form</strong>
              <p className="sub" style={{ marginTop: 2 }}>
                {data?.form
                  ? `Registered: ${data.form.title ?? data.form.formUrl}` +
                    (data.form.dueDate ? ` · due ${data.form.dueDate}` : "") +
                    (data.form.closed ? " · closed" : "")
                  : `No form registered for ${monthLabel}.`}
              </p>
            </div>
            <div className="cluster">
              {data?.form && (
                <a
                  className="btn ghost sm"
                  href={data.form.formUrl}
                  target="_blank"
                  rel="noreferrer"
                >
                  <Icon name="external" size={14} /> Open
                </a>
              )}
              <Button size="sm" icon="link" onClick={() => setShowSetup(true)}>
                {data?.form ? "Update link" : "Register link"}
              </Button>
            </div>
          </div>
        </div>
      )}

      {q.isLoading ? (
        <Skeleton h={420} />
      ) : children.length === 0 ? (
        <EmptyState
          icon="user"
          title="No children yet"
          action={
            <Link className="btn" to="/children">
              Add a child
            </Link>
          }
        >
          Add a child profile, then come back to make their picks.
        </EmptyState>
      ) : days.length === 0 ? (
        <EmptyState icon="calendar" title={`Nothing to order for ${monthLabel} yet`}>
          {me.isAdmin
            ? "Register this month's order form (above) or load its menu."
            : "Once this month's menu or order form is loaded, you can make picks here."}
        </EmptyState>
      ) : (
        <>
          {formClosed && (
            <div className="callout warn">
              <Icon name="alert-circle" size={20} />
              <div className="grow">
                <b>Orders for {monthLabel} are closed.</b>
                <div className="small">
                  The due date ({data!.form!.dueDate}) has passed, so picks are
                  locked. Contact the school directly for a late change.
                </div>
              </div>
            </div>
          )}

          <div className="callout warn">
            <Icon name="alert-circle" size={20} />
            <div className="grow">
              <b>Picking here does not place your order.</b>
              <div className="small">
                Nothing is sent to the school from this page. When you're done,
                open each child's Google&nbsp;Form below, check the answers, and
                click <b>Submit</b> there. That's the only step the school sees.
              </div>
            </div>
          </div>

          <div className="order-summary">
            {children.map((c) => {
              const n = unsetCount(days, local[c.childRuleId]?.picks ?? {});
              const sub = c.submitted;
              const editingThis = editedIds.has(c.childRuleId);
              const unsentThis = unsentIds.has(c.childRuleId);
              const mismatch =
                sub && !editingThis && !unsentThis ? sub.mismatch : [];
              const displayLocked = lockedIds.has(c.childRuleId);
              const showMilk =
                data!.milkOptions.length > 0 && (!sub || editingThis);
              return (
                <div
                  key={c.childRuleId}
                  className={`os-kid${displayLocked ? " locked" : ""}${
                    editingThis || unsentThis || mismatch.length ? " editing" : ""
                  }`}
                >
                  <div className="os-top">
                    <b>{c.label}</b>
                    {editingThis ? (
                      <span className="pill warn">editing</span>
                    ) : unsentThis ? (
                      <span className="pill warn">unsent</span>
                    ) : mismatch.length ? (
                      <span className="pill warn">check</span>
                    ) : sub ? (
                      <span className="pill done">ordered</span>
                    ) : (
                      <span className={`pill ${n === 0 ? "ok" : ""}`}>
                        {n === 0 ? "ready" : `${n} to go`}
                      </span>
                    )}
                  </div>

                  {sub && !editingThis && !unsentThis && (
                    <span className="muted small">
                      submitted {relTime(sub.receivedAt)}
                      {sub.milk ? ` · ${sub.milk}` : ""}
                    </span>
                  )}
                  {mismatch.length > 0 && (
                    <div className="os-mismatch small">
                      <span className="warn-text">
                        The submitted order doesn't match your picks here. If
                        you changed these in the form on purpose, dismiss this.
                      </span>
                      <ul>
                        {mismatch.map((m) => (
                          <li key={m.key}>
                            <b>{m.key === "milk" ? "Milk" : dayLabel(m.key)}</b>:
                            picked {m.picked ?? "nothing"}, submitted{" "}
                            {m.submitted ?? "nothing"}
                          </li>
                        ))}
                      </ul>
                      <button
                        type="button"
                        className="btn ghost sm"
                        disabled={dismissMismatch.isPending}
                        onClick={() => dismissMismatch.mutate(c.childRuleId)}
                      >
                        Dismiss
                      </button>
                    </div>
                  )}
                  {editingThis && (
                    <span className="warn-text small">
                      Editing. Resubmit the form below to send changes to the
                      school.
                    </span>
                  )}
                  {unsentThis && (
                    <span className="warn-text small">
                      You have changes to this order that were never resubmitted.
                    </span>
                  )}

                  {showMilk && (
                    <select
                      className="os-milk"
                      value={local[c.childRuleId]?.milk ?? c.defaultMilk ?? ""}
                      onChange={(e) =>
                        mutateChildren([c.childRuleId], (d) => ({
                          ...d,
                          milk: e.target.value || null,
                        }))
                      }
                    >
                      <option value="">milk…</option>
                      {data!.milkOptions.map((m) => (
                        <option key={m} value={m}>
                          {m}
                        </option>
                      ))}
                    </select>
                  )}

                  {sub && !formClosed && (
                    <div className="cluster os-edit">
                      {editingThis ? (
                        <button
                          type="button"
                          className="btn ghost sm"
                          disabled={discardEdits.isPending}
                          onClick={() => discardEdits.mutate(c.childRuleId)}
                        >
                          Discard changes
                        </button>
                      ) : unsentThis ? (
                        <>
                          <button
                            type="button"
                            className="btn ghost sm"
                            onClick={() => beginEdit(c, "draft")}
                          >
                            Resume editing
                          </button>
                          <button
                            type="button"
                            className="btn ghost sm"
                            disabled={discardEdits.isPending}
                            onClick={() => discardEdits.mutate(c.childRuleId)}
                          >
                            Discard
                          </button>
                        </>
                      ) : (
                        <button
                          type="button"
                          className="btn ghost sm"
                          onClick={() => beginEdit(c, "submitted")}
                        >
                          Edit order
                        </button>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {allLocked && !formClosed && (
            <p className="muted small" style={{ margin: "-4px 0 12px" }}>
              Every child's order for {monthLabel} is in. Use <b>Edit order</b>{" "}
              above to change one before the due date.
            </p>
          )}

          <div className="card order-picker">
            <div className="between order-toolbar">
              <div className="cluster">
                <Button
                  size="sm"
                  variant="secondary"
                  onClick={() => bulkFill("Hot", false)}
                >
                  All Hot
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => bulkFill("Hot", true)}
                >
                  Fill blanks → Hot
                </Button>
                <Button size="sm" variant="ghost" onClick={bulkClear}>
                  Clear
                </Button>
                {children.length > 1 && (
                  <select
                    className="order-copy"
                    value={scope}
                    onChange={(e) => setScope(Number(e.target.value))}
                  >
                    <option value={0}>for: all kids</option>
                    {children.map((c) => (
                      <option key={c.childRuleId} value={c.childRuleId}>
                        for: {c.label}
                      </option>
                    ))}
                  </select>
                )}
              </div>
              <span className="save-note">
                {saveState === "saving"
                  ? "Saving…"
                  : saveState === "saved"
                    ? "Saved"
                    : ""}
              </span>
            </div>

            {byWeek(days).map((wk) => (
              <div key={wk.key} className="order-week">
                <div className="ow-head">{wk.label}</div>
                {wk.days.map((d) => (
                  <DayRow
                    key={d.date}
                    day={d}
                    kids={children}
                    local={local}
                    lockedIds={lockedIds}
                    onKidPick={setKidPick}
                    onKidExtra={setKidExtra}
                  />
                ))}
              </div>
            ))}
          </div>

          {data?.form ? (
            <SendCard
              prefill={prefill.data}
              loading={prefill.isLoading}
              appInbox={me.appInbox}
              closed={formClosed}
              editedIds={needsResubmit}
              onOpened={(id) => markOpened.mutate(id)}
              onCopy={async (url) => {
                try {
                  await navigator.clipboard.writeText(url);
                  toast.success("Link copied");
                } catch {
                  toast.error("Couldn't copy. Open the link and copy from there.");
                }
              }}
            />
          ) : (
            me.isAdmin && (
              <p className="muted small" style={{ marginTop: 16 }}>
                Register this month's order form above to generate a pre-filled
                link for each child.
              </p>
            )
          )}
        </>
      )}

      {showSetup && (
        <SetupModal
          year={year}
          month={month}
          existingUrl={data?.form?.formUrl ?? ""}
          existingDueDate={data?.form?.dueDate ?? null}
          onClose={() => setShowSetup(false)}
          onDone={(msg) => {
            setShowSetup(false);
            qc.invalidateQueries({ queryKey: ["order-draft", year, month] });
            toast.success(msg);
          }}
        />
      )}
    </>
  );
}

function DateCell({ day }: { day: OrderDay }) {
  return (
    <div className="od-date">
      <b>{day.weekday}</b>
      <span className="od-num">{Number(day.date.slice(8))}</span>
      {day.pizzaDay && <span className="tag pizza">Pizza</span>}
    </div>
  );
}

function DayRow({
  day,
  kids,
  local,
  lockedIds,
  onKidPick,
  onKidExtra,
}: {
  day: OrderDay;
  kids: OrderChild[];
  local: Record<number, LocalDraft>;
  lockedIds: Set<number>;
  onKidPick: (id: number, date: string, choice: DraftChoice | null) => void;
  onKidExtra: (id: number, date: string, on: boolean) => void;
}) {
  if (day.noSchool) {
    return (
      <div className="order-day noschool">
        <DateCell day={day} />
        <div className="od-noschool">{day.noServeNote || "No school"}</div>
      </div>
    );
  }

  return (
    <div className={`order-day multi${day.date === todayIso() ? " today" : ""}`}>
      <DateCell day={day} />

      <div className="od-mid">
        <div className="od-dishes">
          {BUYING.filter((c) => day.choices.includes(c)).map((c) => (
            <div key={c} className="od-dish">
              <span className={`tag ${c.toLowerCase()}`}>{c}</span>
              <span className="name">{dishOf(day, c) ?? "not listed"}</span>
            </div>
          ))}
        </div>
      </div>

      <div className="od-kids">
        {kids.map((k) => {
          const pick = local[k.childRuleId]?.picks[day.date];
          const locked = lockedIds.has(k.childRuleId);
          return (
            <div key={k.childRuleId} className={`od-kid${locked ? " locked" : ""}`}>
              <span className="od-kid-name">
                {k.label}
                {locked && <Icon name="check-circle" size={12} />}
              </span>
              <div
                className="od-pick"
                role="group"
                aria-label={`${k.label}, ${day.weekday}`}
              >
                {BUYING.map((c) => (
                  <button
                    key={c}
                    type="button"
                    disabled={locked || !day.choices.includes(c)}
                    aria-pressed={pick?.choice === c}
                    onClick={() =>
                      onKidPick(
                        k.childRuleId,
                        day.date,
                        pick?.choice === c ? null : c,
                      )
                    }
                  >
                    {SHORT[c]}
                  </button>
                ))}
                <button
                  type="button"
                  disabled={locked}
                  aria-pressed={pick?.choice === "No Lunch"}
                  onClick={() =>
                    onKidPick(
                      k.childRuleId,
                      day.date,
                      pick?.choice === "No Lunch" ? null : "No Lunch",
                    )
                  }
                >
                  Skip
                </button>
              </div>
              {day.pizzaDay && day.hasExtraSlice && pick?.choice === "Hot" && (
                <label className="od-extra">
                  <input
                    type="checkbox"
                    disabled={locked}
                    checked={!!pick?.extraSlice}
                    onChange={(e) =>
                      onKidExtra(k.childRuleId, day.date, e.target.checked)
                    }
                  />
                  Extra slice
                </label>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

/** "2026-10-05" → "Mon Oct 5". */
function dayLabel(date: string): string {
  return new Date(`${date}T00:00:00Z`).toLocaleDateString("en-US", {
    weekday: "short",
    month: "short",
    day: "numeric",
    timeZone: "UTC",
  });
}

function relTime(iso: string): string {
  const m = Math.round((Date.now() - new Date(iso).getTime()) / 60000);
  if (m < 1) return "just now";
  if (m < 60) return `${m}m ago`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.round(h / 24)}d ago`;
}

function SendCard({
  prefill,
  loading,
  appInbox,
  closed,
  editedIds,
  onOpened,
  onCopy,
}: {
  prefill?: OrderPrefillPayload;
  loading: boolean;
  appInbox: string;
  closed: boolean;
  editedIds: Set<number>;
  onOpened: (childRuleId: number) => void;
  onCopy: (url: string) => void;
}) {
  const toast = useToast();
  if (loading) return <Skeleton h={120} style={{ marginTop: 16 }} />;
  const children = prefill?.children ?? [];
  if (children.length === 0) return null;

  const copyInbox = async () => {
    try {
      await navigator.clipboard.writeText(appInbox);
      toast.success("Inbox address copied");
    } catch {
      toast.error("Couldn't copy");
    }
  };

  return (
    <div className="card order-send">
      <div className="between">
        <strong>Submit each order on Google Forms</strong>
        <span className="muted small">
          The link opens the school's form pre-filled. Your order isn't placed
          until you tap <b>Submit</b> there.
        </span>
      </div>
      <div className="order-send-list">
        {children.map((c) => {
          const beingEdited = editedIds.has(c.childRuleId);
          if (c.submitted && !beingEdited) {
            return (
              <div key={c.childRuleId} className="order-send-row">
                <div className="osr-main">
                  <b>{c.label}</b>
                  <span className="muted small">
                    {closed
                      ? "Submitted. Orders are closed."
                      : "Order received, nothing to send"}
                  </span>
                </div>
                {c.editUrl && !closed && (
                  <a
                    className="btn ghost sm"
                    href={c.editUrl}
                    target="_blank"
                    rel="noreferrer"
                  >
                    <Icon name="external" size={14} /> Edit on Forms
                  </a>
                )}
              </div>
            );
          }
          if (c.submitted && beingEdited) {
            return (
              <div key={c.childRuleId} className="order-send-row">
                <div className="osr-main">
                  <b>{c.label}</b>
                  <span className="warn-text small">
                    Changed since you submitted. Open the form, check it, and
                    tap <b>Submit</b> again to send the update.
                  </span>
                </div>
                <div className="cluster">
                  {c.editUrl && (
                    <a
                      className="btn ghost sm"
                      href={c.editUrl}
                      target="_blank"
                      rel="noreferrer"
                    >
                      <Icon name="external" size={14} /> Edit on Forms
                    </a>
                  )}
                  <a
                    className="btn sm"
                    href={c.url}
                    target="_blank"
                    rel="noreferrer"
                    onClick={() => onOpened(c.childRuleId)}
                  >
                    <Icon name="external" size={14} /> Resubmit
                  </a>
                </div>
              </div>
            );
          }
          const notes: string[] = [
            `${c.answered} day${c.answered === 1 ? "" : "s"} filled`,
          ];
          if (c.missingDays.length)
            notes.push(
              `${c.missingDays.length} day${c.missingDays.length === 1 ? "" : "s"} still unset`,
            );
          if (c.unmatched.length)
            notes.push(`pick ${c.unmatched.join(", ")} in the form`);
          if (c.openedAt) notes.push(`opened ${relTime(c.openedAt)}`);
          return (
            <div key={c.childRuleId} className="order-send-row">
              <div className="osr-main">
                <b>{c.label}</b>
                {c.missingInfo.length > 0 && (
                  <span className="warn-text small">
                    Needs {c.missingInfo.join(", ")}.{" "}
                    <Link to="/children">edit profile</Link>
                  </span>
                )}
                <span className="muted small">{notes.join(" · ")}</span>
              </div>
              <div className="cluster">
                <button
                  className="btn ghost sm"
                  type="button"
                  onClick={() => onCopy(c.url)}
                >
                  <Icon name="copy" size={14} /> Copy
                </button>
                <a
                  className="btn sm"
                  href={c.url}
                  target="_blank"
                  rel="noreferrer"
                  onClick={() => onOpened(c.childRuleId)}
                >
                  <Icon
                    name={c.openedAt ? "check-circle" : "external"}
                    size={14}
                  />
                  {c.openedAt ? "Re-open" : "Open form"}
                </a>
              </div>
            </div>
          );
        })}
      </div>

      <div className="order-send-foot">
        <Icon name="mail" size={16} />
        <div className="grow small">
          <b>After you submit, forward the receipt.</b> Google emails you a
          "Thanks for filling out this form" confirmation. Forward that whole
          email, one per child, to <code>{appInbox}</code> and the lunch days
          land on your calendar.
        </div>
        <button className="btn ghost sm" type="button" onClick={copyInbox}>
          <Icon name="copy" size={14} /> Copy
        </button>
      </div>
    </div>
  );
}

function SetupModal({
  year,
  month,
  existingUrl,
  existingDueDate,
  onClose,
  onDone,
}: {
  year: number;
  month: number;
  existingUrl: string;
  existingDueDate: string | null;
  onClose: () => void;
  onDone: (msg: string) => void;
}) {
  const [url, setUrl] = useState(existingUrl);
  const [dueDate, setDueDate] = useState(existingDueDate ?? "");
  const [err, setErr] = useState<string | null>(null);

  const register = useMutation({
    mutationFn: () =>
      api.post<{ warnings: string[] }>("/order/forms", {
        year,
        month,
        url,
        dueDate: dueDate || null,
      }),
    onSuccess: (r) =>
      onDone(
        r.warnings.length
          ? `Form registered with ${r.warnings.length} warning${r.warnings.length === 1 ? "" : "s"}`
          : "Form registered",
      ),
    onError: (e) => setErr((e as Error).message),
  });

  return (
    <Modal
      title={`${MONTHS[month - 1]} ${year} order form`}
      onClose={onClose}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button
            icon="link"
            loading={register.isPending}
            disabled={!url.trim()}
            onClick={() => register.mutate()}
          >
            Register form
          </Button>
        </>
      }
    >
      <p className="muted small" style={{ marginBottom: 12 }}>
        Paste the Google Forms responder link for this month (Send → the 🔗 tab).
        The app reads the questions so it can build a pre-filled link per child.
        it never submits anything.
      </p>
      {err && (
        <div className="toast error" style={{ marginBottom: 12 }}>
          <Icon name="alert-circle" size={18} />
          <span className="grow">{err}</span>
        </div>
      )}
      <Field label="Responder link">
        <input
          type="url"
          value={url}
          placeholder="https://docs.google.com/forms/d/e/…/viewform"
          onChange={(e) => setUrl(e.target.value)}
        />
      </Field>
      <Field
        label="Due date (optional)"
        hint="Auto-read from the form title if it says “DUE m/d/yy”. Drives reminder emails."
      >
        <input
          type="date"
          value={dueDate}
          onChange={(e) => setDueDate(e.target.value)}
        />
      </Field>
    </Modal>
  );
}
